import { Children, isValidElement, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { BookOpen, Download } from 'lucide-react'

import manualSource from '../../../docs/handbuch/benutzerhandbuch.md?raw'
import manualPdfUrl from '../../../docs/handbuch/motionlab-benutzerhandbuch.pdf?url'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getCurrentUserRole } from '@/lib/api'
import { parseManual, slugify } from '@/lib/manual'
import { queryKeys } from '@/lib/query-keys'

// Screenshots referenced as bilder/<name>.jpg in the Markdown, bundled by Vite.
const images = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../../docs/handbuch/bilder/*.jpg', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  ).map(([path, url]) => [`bilder/${path.split('/').pop()}`, url]),
)

function textOf(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : isValidElement<{ children?: ReactNode }>(child) ? textOf(child.props.children) : ''))
    .join('')
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function ManualPage() {
  const profileQuery = useQuery({ queryKey: queryKeys.currentUserProfile, queryFn: getCurrentUserRole, retry: false })
  const role = profileQuery.data?.role
  const manual = useMemo(() => (role ? parseManual(manualSource, role) : null), [role])

  const anchors = useMemo(
    () => new Set(manual?.chapters.flatMap((chapter) => [chapter.id, ...chapter.sections.map((section) => section.id)]) ?? []),
    [manual],
  )

  const components = useMemo<Components>(
    () => ({
      h2: ({ children }) => (
        <h2 id={slugify(textOf(children))} className="scroll-mt-4 border-b pb-2">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 id={slugify(textOf(children))} className="scroll-mt-4">
          {children}
        </h3>
      ),
      a: ({ href, children }) => {
        if (href?.startsWith('#')) {
          const id = decodeURIComponent(href.slice(1))
          // Chapters of other roles are not shown; their cross references stay plain text.
          if (!anchors.has(id)) return <>{children}</>
          return (
            <a
              href={href}
              onClick={(event) => {
                event.preventDefault()
                scrollTo(id)
              }}
            >
              {children}
            </a>
          )
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      },
      img: ({ src, alt }) => {
        const url = typeof src === 'string' ? images[src] ?? src : undefined
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className="not-prose block">
            {/* Not lazy: images loading later would move the targets of the table of contents. */}
            <img src={url} alt={alt ?? ''} decoding="async" className="my-4 max-h-[42rem] w-auto max-w-full rounded-lg border shadow-sm" />
          </a>
        )
      },
      table: ({ children }) => (
        <div className="overflow-x-auto">
          <table>{children}</table>
        </div>
      ),
    }),
    [anchors],
  )

  if (profileQuery.isLoading || !manual) {
    return <Skeleton className="h-8 w-64" />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <BookOpen className="h-6 w-6 text-primary" />
            Handbuch
          </h1>
          <p className="text-sm text-muted-foreground">Anleitung zu allen Bereichen und Abläufen der App.</p>
        </div>
        <Button asChild variant="outline">
          <a href={manualPdfUrl} download="motionLAB-Benutzerhandbuch.pdf">
            <Download className="mr-2 h-4 w-4" />
            Als PDF herunterladen
          </a>
        </Button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <nav aria-label="Inhalt" className="lg:sticky lg:top-0 lg:max-h-[calc(100vh-12rem)] lg:self-start lg:overflow-y-auto">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Inhalt</p>
          <ol className="space-y-1 text-sm">
            {manual.chapters.map((chapter) => (
              <li key={chapter.id}>
                <button type="button" className="text-left font-medium hover:text-primary" onClick={() => scrollTo(chapter.id)}>
                  {chapter.title}
                </button>
                {chapter.sections.length > 0 && (
                  <ol className="mt-1 mb-2 hidden space-y-1 border-l pl-3 text-muted-foreground lg:block">
                    {chapter.sections.map((section) => (
                      <li key={section.id}>
                        <button type="button" className="text-left hover:text-primary" onClick={() => scrollTo(section.id)}>
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <article className="prose max-w-none dark:prose-invert prose-headings:font-semibold prose-table:text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {manual.intro}
          </ReactMarkdown>
          {manual.chapters.map((chapter) => (
            <ReactMarkdown key={chapter.id} remarkPlugins={[remarkGfm]} components={components}>
              {chapter.markdown}
            </ReactMarkdown>
          ))}
        </article>
      </div>
    </div>
  )
}
